/**
 * Guest FAQs API — Phase 6 sub-commit 6a (13 Jun 2026).
 *
 * TanStack Query wrapper around `GET /v1/faqs` on towninsider-api
 * (sub-commit 5). Backs the offline AI fallback (sub-commit 6b)
 * which fuzzy-matches a guest's chat query against this catalogue
 * when shouldSend is false.
 *
 * Architecture mirrors announcements.ts:
 *   - apiFetch boundary with inline Zod schema
 *   - useAuth() for token + userId
 *   - Identity-scoped query key via exported factory
 *
 * --------------------------------------------------------------------
 * Identity-scoped query key
 * --------------------------------------------------------------------
 *
 *   faqsQueryKey(userId) → ['faqs', userId]
 *
 * Account A's FAQs cache under one key, account B's under another —
 * cross-account bleed structurally impossible. Same posture as
 * inboxQueryKey / unreadCountQueryKey.
 *
 * Exported so the prefetch hook (prefetch.ts) can construct the key
 * without duplicating the literal. Single source of truth.
 *
 * The server resolves the partner from the guest's users.partner_ref,
 * so the URL itself doesn't carry a partner segment — userId alone is
 * sufficient cache discrimination, since one user maps to at most one
 * partner via attribution.
 *
 * --------------------------------------------------------------------
 * staleTime — 1 hour
 * --------------------------------------------------------------------
 *
 * FAQs are hand-curated by partners and change rarely (hourly at the
 * upper bound of typical update cadence; weekly more realistic). The
 * once-per-day prefetch (sub-commit 2) populates the SW cache eagerly;
 * a 1h staleTime keeps the in-memory cache "fresh" from the hook's
 * POV so the offline matcher consumer doesn't trigger redundant
 * refetches whenever the FAQ list is re-read.
 *
 * Mirrors the picks.ts 4h precedent (catalogue data, slow-changing)
 * rather than the announcements.ts 30s precedent (push-driven,
 * real-time visibility matters).
 *
 * --------------------------------------------------------------------
 * Wire shape
 * --------------------------------------------------------------------
 *
 * Server projects out the internal admin fields (active, archivedAt,
 * expiresAt, createdAt, updatedAt) — see src/routes/faqs.ts on
 * towninsider-api. The guest wire shape is what the offline matcher
 * needs: id (cache keying), question (matching), answer (display),
 * category and venueId (any future client-side grouping).
 */

import { useAuth } from '@clerk/clerk-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '../api';

// ─── Wire schema ─────────────────────────────────────────────────────

const guestFaqSchema = z.object({
  id: z.string().min(1),
  partnerId: z.string().min(1),
  venueId: z.string().nullable(),
  question: z.string().min(1),
  answer: z.string().min(1),
  category: z.string().nullable(),
  displayOrder: z.number().int(),
});

export type GuestFaq = z.infer<typeof guestFaqSchema>;

const faqsResponseSchema = z.object({
  faqs: z.array(guestFaqSchema),
});

export type FaqsResponse = z.infer<typeof faqsResponseSchema>;

// ─── Query key factory (EXPORTED for prefetch.ts) ────────────────────

export function faqsQueryKey(
  userId: string | null,
): readonly ['faqs', string | null] {
  return ['faqs', userId] as const;
}

// ─── Fetcher ─────────────────────────────────────────────────────────

interface FetcherArgs {
  getToken: () => Promise<string | null>;
}

export async function getFaqs({
  getToken,
}: FetcherArgs): Promise<FaqsResponse> {
  return apiFetch('/v1/faqs', {
    schema: faqsResponseSchema,
    getToken,
  });
}

// ─── Hook ────────────────────────────────────────────────────────────

export interface UseFaqsOptions {
  /**
   * Gate the fetch on top of the built-in signed-in check. Default
   * true. Pass false in tests or in contexts that want to defer.
   */
  enabled?: boolean;
}

/**
 * The FAQ-catalogue hook. Returns the guest's attributed partner's
 * active, non-expired FAQs. Unattributed guests (bare-URL onboarding
 * via Morwenna) get an empty array — the server returns { faqs: [] }
 * rather than 404 so the hook never errors on the no-partner case.
 *
 * Primary consumer: the offline AI fallback in MePage's handleSend
 * (sub-commit 6b), via the matchFaq() utility in
 * src/lib/offlineFaqMatcher.ts.
 */
export function useFaqs(
  options?: UseFaqsOptions,
): UseQueryResult<FaqsResponse> {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<FaqsResponse>({
    queryKey: faqsQueryKey(userId ?? null),
    queryFn: () => getFaqs({ getToken }),
    enabled: !!isSignedIn && !!userId && (options?.enabled ?? true),
    staleTime: 60 * 60 * 1000,
  });
}
